import { useEffect, useMemo, useState } from 'react';
import PopOnMe from '../../../../components/elements/poponme';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { getFormattedDate } from '../../../../utils/miscFunctions';
import useUserAccount from '../../../../hooks/userAccountHook';
import GTable from '../../../../components/elements/GTable';
import useModal from '../../../../hooks/modalHook';
import Loader from '../../../../components/Loader';
import { Edit, Eye, Handshake, Send } from 'lucide-react';
import Button from '../../../../components/form-control/Button';
import { GTableActionColumn, ITableRowAction } from '../../../../components/elements/GTable/GTable';
import { INTERMEDIARY_TYPES, REDACT_INFO_TYPES } from '../../../../resources/enums';
import RedactInfo from '../../../../components/elements/RedactInfo';
import useIntermediaries from '../../../../hooks/intermediariesHook';

export interface BrokerInsurerIntegration {
  id: number;
  apikey: string;
  token: string;
  brokerId: number;
  brokerName: string;
  branchCode: string;
  intermediaryCode: string;
  insurerId: number;
  insurerName: string;
  type: INTERMEDIARY_TYPES;
  comprehensiveCommissionRate: number;
  thirdPartyCommissionRate: number;
  createdOn: string;
  updatedOn: string | null;
  insurerContactEmails?: string;
  issuanceMode: string;
}

interface IntermediariesProps {
  isSettings?: boolean;
}

const Intermediaries = ({ isSettings = false }: IntermediariesProps): JSX.Element => {
  const { user } = useUserAccount();
  const { ModalComponent } = useModal();
  const { intermediaries, IntermediariesModal, handleCreateNewIntermediary, editIssuanceMode, sendMIDAccessRequestToUnderwriter, isIntermidiaryLoading, PolicyIssuanceModeBadge } = useIntermediaries();
  const [searchQuery, setSearchQuery] = useState('');
  const filteredIntermediaries = useMemo(() => {
    if (!searchQuery) return intermediaries;

    const query = searchQuery.toLowerCase();
    return intermediaries?.filter((intermediary: any) => {
      return (
        (intermediary.insurerName?.toLowerCase().includes(query)) ||
        (intermediary.brokerName?.toLowerCase().includes(query))
      );
    });
  }, [intermediaries, searchQuery]);

  // Action available to an insurer: change a broker's policy issuance mode.
  const getInsurerRowActions = (intermediary: BrokerInsurerIntegration): ITableRowAction[] => ([
    {
      label: 'Edit Issuance Mode',
      icon: <Edit className="h-4 w-4" />,
      callback: () => editIssuanceMode(intermediary),
      data: intermediary
    }
  ]);

  const getTableRowActions = (intermediary: BrokerInsurerIntegration): ITableRowAction[] => {
    const actions = [
      {
        label: 'View',
        icon: <Eye className="h-4 w-4" />,
        callback: () => {
          handleCreateNewIntermediary(false, intermediary, true);
        },
        data: intermediary
      },
      {
        label: 'Edit',
        icon: <Edit className="h-4 w-4" />,
        callback: () => {
          handleCreateNewIntermediary(false, intermediary);
        },
        data: intermediary
      },
      {
        label: 'Request MID access',
        icon: <Send className="h-4 w-4" />,
        callback: () => {
          sendMIDAccessRequestToUnderwriter(intermediary);
        },
        data: intermediary
      }
    ];

    return actions;
  };

  const columnsForBroker = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Insurer',
            accessor: 'insurerName',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Branch Code',
            accessor: 'branchCode',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Intermediairy Code',
            accessor: 'intermediaryCode',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'API Key',
            accessor: 'apikey',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? (
                    <RedactInfo
                      infoType={REDACT_INFO_TYPES.API_KEY}
                      info={row.cell.value}
                      dataId={row.row.original.id}
                      dataType="intermediary"
                      clickable={user?.isBroker() || user?.isOwner()}
                      totalToRedact={14}
                    />
                  ) : (
                    '-'
                  )}
                </span>
              );
            }
          },
          {
            Header: 'Issuance Mode',
            accessor: 'issuanceMode',
            Cell: (row: any) => PolicyIssuanceModeBadge(row.cell.value)
          },
          {
            Header: 'Updated On',
            accessor: 'updatedOn',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
              );
            }
          },
          {
            id: 'Action',
            Cell: (row: any) => {
              return (
                user?.isBroker() && (
                  <GTableActionColumn actions={getTableRowActions(row.row.original)} />
                )
              );
            }
          }
        ]
      }
    ],
    []
  );

  // Insurer view: a list of the insurer's brokers with their issuance mode and an edit action.
  const columnsForInsurer = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Broker',
            accessor: 'brokerName',
            Cell: (row: any) => <span>{row.cell.value ? row.cell.value : '-'}</span>
          },
          {
            Header: 'Branch Code',
            accessor: 'branchCode',
            Cell: (row: any) => <span>{row.cell.value ? row.cell.value : '-'}</span>
          },
          {
            Header: 'Intermediairy Code',
            accessor: 'intermediaryCode',
            Cell: (row: any) => <span>{row.cell.value ? row.cell.value : '-'}</span>
          },
          {
            Header: 'Issuance Mode',
            accessor: 'issuanceMode',
            Cell: (row: any) => PolicyIssuanceModeBadge(row.cell.value)
          },
          {
            Header: 'Updated On',
            accessor: 'updatedOn',
            Cell: (row: any) => <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
          },
          {
            Header: 'Action',
            id: 'Action',
            Cell: (row: any) => <GTableActionColumn actions={getInsurerRowActions(row.row.original)} />
          }
        ]
      }
    ],
    []
  );

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Insurer',
            accessor: 'insurerName',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Branch Code',
            accessor: 'branchCode',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Intermediairy Code',
            accessor: 'intermediaryCode',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Token',
            accessor: 'token',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? (
                    <RedactInfo
                      infoType={REDACT_INFO_TYPES.TOKEN}
                      info={row.cell.value}
                      dataId={row.row.original.id}
                      dataType="intermediary"
                      clickable={user?.isBroker() || user?.isOwner()}
                      totalToRedact={20}
                    />
                  ) : (
                    '-'
                  )}
                </span>
              );
            }
          },
          {
            Header: 'API Key',
            accessor: 'apikey',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? (
                    <RedactInfo
                      infoType={REDACT_INFO_TYPES.API_KEY}
                      info={row.cell.value}
                      dataId={row.row.original.id}
                      dataType="intermediary"
                      clickable={user?.isBroker() || user?.isOwner()}
                      totalToRedact={20}
                    />
                  ) : (
                    '-'
                  )}
                </span>
              );
            }
          },
          {
            Header: 'Updated On',
            accessor: 'updatedOn',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
              );
            }
          },
          {
            Header: "Action",
            id: 'Action',
            Cell: (row: any) => {
              return (
                user?.isBroker() && (
                  <GTableActionColumn actions={getTableRowActions(row.row.original)} />
                )
              );
            }
          }
        ]
      }
    ],
    []
  );

  return (
    <div className=" bg-white dark:bg-darkBg min-h-full">
      {isIntermidiaryLoading ? (
        <Loader message="Fetching all intermediaries. Please wait..." brollyLoaderFixed />
      ) : (
        <div className="bg-white dark:bg-darkBg">
          {user?.isBroker() && !isSettings && (
            <div className="flex justify-end md:pt-0">
              <Button
                onClick={() => handleCreateNewIntermediary(true)}
                label="Add Company"
                icon={<Handshake className="w-5 h-5" />}
                bgColor="primary"
                className="flex gap-2 rounded-full border bg-[#FCF6E9] border-[#FBC02D] px-3 py-2"
              />
            </div>
          )}

          <div className="overflow-x-auto mt-5 bg-white dark:bg-darkBg">
            <GTable
              columns={user.isBroker() ? columnsForBroker : user.isInsurer() ? columnsForInsurer : columns}
              onSearch={(searchValue) => setSearchQuery(searchValue)}
              searchPlaceholder='Search by name'
              data={filteredIntermediaries ?? []}
              isRowLinked={false}
              loading={isIntermidiaryLoading}
              // Empty-state text ("No <X> found") mirrors the column set — an
              // insurer's Brokers tab shows brokers, a broker's Insurers tab
              // shows insurance companies, everyone else sees the generic term.
              tableType={user.isInsurer() ? 'broker' : user.isBroker() ? 'insurance company' : 'intermediary'}
              classes={'max-h-[400px]'}
              stickyHeader
            />
          </div>
        </div>
      )}
      <ModalComponent />
      <IntermediariesModal />
    </div>
  );
};

export default Intermediaries;
